import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ArrowDown, ArrowRight, ArrowUp, ArrowUpRight, Check, Clapperboard, CornerDownRight, MessageCircle, PenLine, Pencil, RotateCcw, Sparkles, Square, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { useTeamBrand } from '../branding/TeamBrandContext';
import BorderGlow from '../components/BorderGlow';
import { streamCreationIntake } from './creationIntakeClient';
import CreationConfirmationBar from './CreationConfirmationBar';
import CreationActivity, { useCreationActivity } from './CreationActivity';
import { MessageReferences, ReferenceDrafts, ReferenceUploadButton, savedReferences, useCreationReferences } from './CreationReferences';
import CreationQuestions, { cleanAnswers, formatAnswers, validQuestions } from './CreationQuestions';
import { previewIntake } from './previewIntake';
import { canConfirmBrief, formatCreationBrief } from './creationBrief';
import { appendIntakeInput, conversationReferences, intakeRound, packIntakeConversation, selectScreeningMode, summarizeIntake } from './intakeConversation';
import './HomeCreationIntake.css';

const STORAGE_KEY = 'xiaoying:home:intake:v1';
const CREATION_PLACEHOLDER = '可以跟小影说，你想做什么视频？\n比如短视频、短剧、广告片、宣传片、口播、Vlog、产品讲解、预告片、MV、长片…';
const MODES = {
  writing: { label: '完整剧本', destination: '写作台', description: '从人物与故事大纲到分集正文，逐步打磨一套短剧剧本。', steps: ['确定故事', '规划分集', '写出正文'], Icon: PenLine },
  canvas: { label: '快速出片', destination: '制作画布', description: '直接准备短片脚本、素材与分镜，确认方案后生成画面。', steps: ['整理需求', '准备分镜', '生成画面'], Icon: Clapperboard },
};
const EXAMPLES = [
  { label: '写一套完整剧本', prompt: '写一套关于替嫁复仇的短剧剧本', Icon: PenLine },
  { label: '做一条30秒宣传片', prompt: '做一条30秒的咖啡店宣传片', Icon: Clapperboard },
  { label: '还没想好，先聊聊', prompt: '只有一个模糊想法，想先聊聊', Icon: MessageCircle },
];
const token = () => `home-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
const requestedQuestions = result => result?.screening?.requestedQuestions === true && validQuestions(result.questions) && result.questions.length > 0;

function readDraft(owner, preview) {
  if (preview) return {};
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (saved?.version !== 1 || (saved.owner !== owner && !(saved.owner === 'guest' && saved.resume))) return {};
    if (!Array.isArray(saved.messages) || saved.messages.length > 200 || saved.messages.some(item => !item || !['user', 'assistant'].includes(item.role) || typeof item.content !== 'string' || item.content.length > 16000) || saved.messages.reduce((sum, item) => sum + item.content.length, 0) > 160000) return {};
    if (typeof saved.draft !== 'string' || saved.draft.length > 4000 || (saved.modePreference && !MODES[saved.modePreference])) return {};
    if (saved.decision && !validDecision(saved.decision)) return {};
    saved.references = savedReferences(saved.references).slice(0, 4);
    saved.messages = saved.messages.map(item => ({ ...item, ...(item.references ? { references: savedReferences(item.references) } : {}) }));
    if (saved.messages.at(-1)?.role === 'assistant' && intakeRound(saved.messages) >= 3 && !canConfirmBrief(saved.decision) && !requestedQuestions(saved.decision)) {
      return { ...saved, decision: summarizeIntake(saved.messages, saved.decision), resume: false };
    }
    return { ...saved, answers: cleanAnswers(validQuestions(saved.decision?.questions) ? saved.decision.questions : [], saved.answers) };
  } catch { return {}; }
}

function validDecision(result) {
  return result && typeof result.reply === 'string' && Boolean(result.reply.trim()) && (result.mode === null || Boolean(MODES[result.mode]))
    && typeof result.ready === 'boolean' && (!result.ready || (typeof result.summary === 'string' && Boolean(result.summary.trim()) && typeof result.title === 'string'))
    && (!result.suggestions || (Array.isArray(result.suggestions) && result.suggestions.every(item => typeof item === 'string')))
    && (!result.brief || ([2, 3].includes(result.brief.version) && Array.isArray(result.brief.fields) && result.brief.fields.length <= 9
      && result.brief.fields.every(item => item && typeof item.key === 'string' && typeof item.label === 'string' && typeof item.value === 'string' && ['missing', 'pending', 'suggested', 'provided', 'delegated'].includes(item.status))));
}

export default function HomeCreationIntake({ authenticated, preview = false, owner = 'guest', inputRef, onReview }) {
  const navigate = useNavigate();
  const brand = useTeamBrand();
  const [initial] = useState(() => readDraft(owner, preview));
  const [dialogOpen, setDialogOpen] = useState(Boolean(initial.resume || initial.messages?.length));
  const [resetPending, setResetPending] = useState(false);
  const launchRef = useRef(null);
  const dialogInputRef = useRef(null);
  const launcherHeight = useRef(360);
  const wasOpen = useRef(dialogOpen);
  const interrupted = initial.messages?.at(-1)?.role === 'user' && !initial.resume;
  const [messages, setMessages] = useState(initial.messages || []);
  const attachments = useCreationReferences(initial.references, { preview, used: conversationReferences(messages).length });
  const [draft, setDraft] = useState(initial.draft || '');
  const [decision, setDecision] = useState(initial.decision || null);
  const [answers, setAnswers] = useState(initial.answers || {});
  const [modePreference, setModePreference] = useState(initial.modePreference || null);
  const [busy, setBusy] = useState(false);
  const [partialReply, setPartialReply] = useState('');
  const activity = useCreationActivity(busy, partialReply);
  const [stopped, setStopped] = useState(false);
  const [awayFromReply, setAwayFromReply] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(interrupted ? '上次沟通未完成，想法已保留。重试会先压缩历史再整理。' : '');
  const [referenceError, setReferenceError] = useState(false);
  const [notice, setNotice] = useState('');
  const [createError, setCreateError] = useState('');
  const [failedMessages, setFailedMessages] = useState(interrupted ? initial.messages : null);
  const lock = useRef(false);
  const alive = useRef(true);
  const resumed = useRef(false);
  const resumePending = useRef(Boolean(initial.resume));
  const submission = useRef(null);
  const conversationRef = useRef(null);
  const followReply = useRef(true);
  const requestRef = useRef(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const ready = canConfirmBrief(decision) && !busy && !error && !stopped && messages.at(-1)?.role === 'assistant';
  const active = messages.length > 0;
  const questions = validQuestions(decision?.questions) ? decision.questions : [];
  const suggestions = messages.length
    ? (decision?.suggestions || []).map(text => ({ label: text, prompt: text }))
    : EXAMPLES;

  const saveDraft = state => {
    if (preview) return;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, owner, messages, draft, references: attachments.serializable(), decision, answers, modePreference, resume: resumePending.current, ...state })); } catch { /* 当前页面仍保留草稿。 */ }
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      const request = requestRef.current;
      // StrictMode 会立即重放 effect；只取消真正离开页面的请求。
      queueMicrotask(() => { if (!alive.current) request?.abort(); });
    };
  }, []);
  useEffect(() => { saveDraft({}); /* 每轮保留，包括登录前的输入。 */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, draft, decision, answers, modePreference, attachments.items]);
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const log = conversationRef.current;
      if (log && followReply.current) {
        const result = !busy && log.querySelector('.creation-questions, .home-intake__review');
        if (result) log.scrollTop += result.getBoundingClientRect().top - log.getBoundingClientRect().top - 24;
        else log.scrollTop = log.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [messages.length, partialReply, busy]);
  useEffect(() => {
    const input = inputRef.current;
    if (input?.tagName === 'TEXTAREA') { input.style.height = 'auto'; input.style.height = `${Math.min(input.scrollHeight, 164)}px`; }
  }, [draft, messages.length, inputRef, dialogOpen]);
  useEffect(() => {
    if (!dialogOpen && wasOpen.current) launchRef.current?.focus();
    wasOpen.current = dialogOpen;
  }, [dialogOpen]);

  const consult = async (conversation, preference = modePreference, { retry = false, finalize = false, questionMode } = {}) => {
    if (lock.current) return;
    finalize = finalize || (decision?.brief?.version === 3 && canConfirmBrief(decision));
    lock.current = true;
    const controller = new AbortController();
    requestRef.current = controller;
    followReply.current = true; setAwayFromReply(false);
    resumePending.current = true;
    setBusy(true); setError(''); setReferenceError(false); setCreateError(''); setFailedMessages(null);
    setPartialReply(''); setStopped(false); draftRef.current = '';
    setMessages(conversation); setDraft('');
    const round = intakeRound(conversation);
    const packed = packIntakeConversation(conversation, retry);
    setNotice(packed.compacted ? '已合并重复补充，压缩发送历史；原始对话仍保留在这里。' : '');
    saveDraft({ messages: conversation, draft: '', decision: null, modePreference: preference, resume: true });
    const accept = result => {
      setDecision(result); setAnswers({}); resumePending.current = false; setModePreference(null);
      const next = [...conversation, { role: 'assistant', content: result.reply }];
      setMessages(next); setPartialReply('');
      saveDraft({ messages: next, draft: draftRef.current, decision: result, answers: {}, modePreference: null, resume: false });
    };
    try {
      let result;
      if (preview) {
        result = previewIntake(conversation, preference, finalize || retry, questionMode);
      } else {
        result = await streamCreationIntake({ messages: packed.messages, modePreference: preference, round, finalize: finalize || retry,
          ...(questionMode ? { questionMode } : {}),
          ...(decision?.brief?.mode && [2, 3].includes(decision.brief.version) ? { previousBrief: decision.brief } : {}) }, {
          signal: controller.signal,
          onReplyDelta: text => { if (alive.current && !controller.signal.aborted) setPartialReply(value => value + text); },
        });
      }
      if (!alive.current) return;
      if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      if (!validDecision(result)) throw new Error('需求回复暂时无法读取，请重试。');
      if (!canConfirmBrief(result) && !requestedQuestions(result) && (round >= 3 || finalize || retry)) result = summarizeIntake(conversation, result, { round });
      accept(result);
    } catch (failure) {
      if (!alive.current) return;
      const cancelled = controller.signal.aborted;
      // 旧接口长度限制、网络或模型失败时，重试后用原话确认；不把本地摘要伪装成 AI 回复。
      if (!cancelled && ![401, 403, 422, 429].includes(failure.response?.status) && (retry || finalize || round >= 3)) {
        accept(summarizeIntake(conversation, null, { round, fallback: true }));
        setNotice('AI 整理未完成，已切换为原话确认。请选择创作方式，想法会一起带入。');
        return;
      }
      setStopped(cancelled);
      setReferenceError(failure.response?.status === 422);
      if (!cancelled) setError(getErrorMessage(failure, '需求助手暂时不可用，想法已保留，可以重试或继续补充。'));
      setFailedMessages(conversation);
      resumePending.current = failure.response?.status === 401;
      saveDraft({ messages: conversation, draft: draftRef.current, decision: null, modePreference: preference, resume: resumePending.current });
      if (failure.response?.status === 401) navigate('/login', { state: { from: '/home?resume=creation' } });
    } finally { lock.current = false; requestRef.current = null; if (alive.current) setBusy(false); }
  };

  const send = (value, preference = modePreference, options) => {
    if (attachments.blocked) { setNotice('请等待图片上传完成；上传失败的图片可以重试或移除。'); return; }
    let text = (value ?? draft).trim();
    const references = attachments.serializable();
    if (value == null && questions.length && messages.at(-1)?.role === 'assistant'
      && Object.values(cleanAnswers(questions, answers)).some(answer => answer.selected.length || answer.custom.trim())) {
      text = `${formatAnswers(questions, answers)}${text ? `\n\n补充：${text}` : ''}`;
    }
    if (!text && references.length) text = '请参考这些图片，和我一起完善创作想法。';
    if (text.length > 4000) { setNotice('选择和补充合计超过 4,000 字，请精简后继续。'); return; }
    if (!text || lock.current) { if (!text) inputRef.current?.focus(); return; }
    const conversation = appendIntakeInput(messages, text, references);
    if (!authenticated && !preview) {
      resumePending.current = true;
      setDraft(text);
      saveDraft({ draft: text, resume: true });
      navigate('/login', { state: { from: '/home?resume=creation' } }); return;
    }
    attachments.clear();
    consult(conversation, preference, options);
  };
  const retryConversation = () => draft.trim() || attachments.items.length ? send(draft, modePreference, { retry: true }) : consult(failedMessages || messages, modePreference, { retry: true });
  const beforeSelectImages = () => {
    if (authenticated || preview) return true;
    saveDraft({ resume: false });
    navigate('/login', { state: { from: '/home?resume=creation' } });
    return false;
  };

  useEffect(() => {
    if (!authenticated || preview || !initial.resume || resumed.current) return;
    resumed.current = true;
    if (initial.messages?.at(-1)?.role === 'user' && !initial.draft) consult(initial.messages, initial.modePreference);
    else if (initial.draft) send(initial.draft);
    // 登录回来只恢复一次，状态变化不能重复发送。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  const create = async () => {
    if (lock.current || !ready || !MODES[decision?.mode] || draft.trim() || attachments.items.length) return;
    const chosenMode = decision.mode;
    const userMessages = messages.filter(item => item.role === 'user');
    if (!userMessages.length) return;
    lock.current = true; setCreating(true); setCreateError('');
    const transcript = packIntakeConversation(messages.at(-1)?.role === 'assistant' ? messages.slice(0, -1) : messages, true).messages.map(item => `${item.role === 'user' ? '用户' : '创作助手'}：${item.content}${item.references?.length ? `\n用户上传的参考图片：\n${item.references.map(ref => `${ref.name}：${ref.url}`).join('\n')}` : ''}`).join('\n\n');
    const summary = formatCreationBrief(decision);
    const content = `${summary}\n\n后续计划：\n${decision.brief.outline.map((item, index) => `${index + 1}. ${item}`).join('\n')}\n\n首页对话记录（用户要求以最新表达为准，助手建议须以已确认简报为准）：\n${transcript}`;
    const message = `用户已确认以下创作方案，请沿用已明确的要求，按${chosenMode === 'canvas' ? '制作画布的脚本与分镜' : '完整短剧剧本写作'}流程开始创作。无需重复询问已回答的问题；出现冲突时再确认。\n\n已确认的创作简报：\n${content}`;
    const title = decision?.title?.trim() || userMessages[0].content.slice(0, 20) || '新的创作';
    const contentType = chosenMode === 'canvas' ? decision?.content_type || 'SHORT_VIDEO' : null;
    const references = conversationReferences(userMessages).map(({ token }) => ({ token }));
    const source = { type: 'brief', content, ...(references.length ? { references } : {}) };
    const signature = JSON.stringify({ chosenMode, message, title, contentType, references });
    if (submission.current?.signature !== signature) submission.current = { signature, key: token() };
    // 关键字段优先放入 2,000 字交接摘要，完整方案和对话同时保存在需求节点及首轮消息中。
    const briefReply = summary.slice(0, 2000);
    const homeEntry = { id: submission.current.key, message, mode: chosenMode, contentType, briefReply, brief: decision.brief,
      conversation: messages.map(item => ({ ...item, ...(item.references ? { references: item.references.map(({ token, ...ref }) => ref) } : {}) })), confirmed: true };
    try {
      let id = submission.current.id;
      if (preview) id = chosenMode === 'canvas' ? 'preview-canvas' : 'preview-new';
      else if (!id) {
        const payload = chosenMode === 'canvas' ? { name: title, source } : { title };
        const response = await api.post(chosenMode === 'canvas' ? '/canvas' : '/agent/projects', payload,
          { headers: { 'Idempotency-Key': submission.current.key } });
        id = response.data?.id;
        if (!id) throw new Error('未收到项目创建结果，请重试。');
        submission.current.id = id;
      }
      if (!alive.current) return;
      if (!preview) { try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* 无持久草稿 */ } }
      navigate(`${preview ? '/prototype/vnext' : ''}/${chosenMode === 'canvas' ? 'canvas' : 'writing'}/${encodeURIComponent(id)}`, { state: { homeEntry } });
    } catch (failure) { setCreateError(getErrorMessage(failure, '项目暂时没有创建成功，需求还在，可以重试。')); }
    finally { lock.current = false; if (alive.current) setCreating(false); }
  };

  const restart = () => {
    if (lock.current) return;
    resumePending.current = false;
    attachments.clear(); setReferenceError(false);
    setMessages([]); setDraft(''); setDecision(null); setAnswers({}); setModePreference(null); setError(''); setNotice(''); setCreateError(''); setFailedMessages(null); setPartialReply(''); setStopped(false); submission.current = null;
    inputRef.current?.focus();
  };

  const editBrief = label => {
    setDraft(value => `${value ? `${value}\n` : ''}我想调整「${label}」：`);
    inputRef.current?.focus();
  };
  const changeMode = key => {
    if (key === decision?.mode || !ready || draft.trim()) return;
    setModePreference(key);
    setDecision(selectScreeningMode(decision, key));
    setNotice(`已选择${MODES[key].label}，核心想法和已知要求会一起带入，细节进入后继续完善。`);
  };

  return <>
    {!dialogOpen ? <div className="home-intake home-intake-launcher" ref={element => { if (element) launcherHeight.current = element.getBoundingClientRect().height; }}>
      <BorderGlow
        edgeSensitivity={30}
        glowColor="266 42 66"
        backgroundColor="var(--vnext-composer)"
        borderRadius={28}
        glowRadius={40}
        glowIntensity={1}
        coneSpread={25}
        animated={false}
        colors={['#9784c2', '#b88eb9', '#7f99c5']}
        fillOpacity={0.2}
        className="home-intake-launcher__glow"
      >
      <form className="home-intake-launcher__form" aria-label="发送创作需求" onSubmit={event => { event.preventDefault(); setDialogOpen(true); if (!active && (draft.trim() || attachments.items.length)) send(); }}>
          <label className="sr-only" htmlFor="home-creation-launcher">描述你的创作想法</label>
          <textarea id="home-creation-launcher" ref={element => { if (element) inputRef.current = element; }} value={draft} onChange={event => setDraft(event.target.value)} maxLength={4000} rows={4} placeholder={brand.text(active ? '继续告诉小影，你想补充或调整什么…' : CREATION_PLACEHOLDER)}
            onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); setDialogOpen(true); if (!active && (draft.trim() || attachments.items.length)) send(); } }} />
          <ReferenceDrafts attachments={attachments} />
          <div className="home-intake-launcher__toolbar">
            <div className="home-intake-launcher__tools">
              <ReferenceUploadButton onFiles={attachments.add} onBeforeSelect={beforeSelectImages} disabled={creating || attachments.items.length >= 4 || conversationReferences(messages).length + attachments.items.length >= 12} />
              {active ? <span className="home-intake-launcher__saved" role="status"><span aria-hidden="true" />{busy ? '正在整理想法…' : ready ? '方案待确认' : '对话已保留'}</span>
                : <span className="home-intake-launcher__hint"><Sparkles size={13} aria-hidden="true" />{brand.text('说说内容和用途，小影帮你梳理')}</span>}
            </div>
            <button ref={launchRef} type="submit" className="home-intake-launcher__start" aria-haspopup="dialog"><span>{active ? '继续上次对话' : '开始聊聊'}</span><span className="home-intake-launcher__arrow" aria-hidden="true"><ArrowRight size={15} /><ArrowRight size={15} /></span></button>
          </div>
      </form>
      </BorderGlow>
      <div className="home-intake-launcher__meta">
        {onReview ? <button type="button" className="home-intake-launcher__review" onClick={onReview}><span>已有剧本？</span>上传评估<ArrowUpRight size={12} aria-hidden="true" /></button> : <span />}
        {draft.length > 3600 ? <span className="home-intake-launcher__count">{draft.length} / 4,000</span>
          : active ? <span className="home-intake-launcher__topic"><MessageCircle size={12} /><span>{decision?.title || messages.find(item => item.role === 'user')?.content}</span></span>
            : <span className="home-intake-launcher__shortcut">Enter 开始对话 · Shift + Enter 换行</span>}
      </div>
      {preview && <p className="home-intake__preview">交互预览 · 示例回复，未调用 AI</p>}
    </div> : <div className="home-intake-launcher-spacer" style={{ height: launcherHeight.current }} aria-hidden="true" />}
    <Dialog open={dialogOpen} onClose={() => { if (!creating) { setResetPending(false); setDialogOpen(false); } }} initialFocus={dialogInputRef} className="home-intake-dialog">
    <DialogBackdrop transition className="home-intake-dialog__backdrop" />
    <div className="home-intake-dialog__position">
    <DialogPanel transition className={`home-intake home-intake-dialog__panel${active ? ' is-active' : ''}${ready ? ' is-ready' : ''}`}>
    <header className="home-intake__intro"><span className="home-intake__mark"><Sparkles size={18} /></span><div><DialogTitle as="strong">{brand.text('小影创作助手')}</DialogTitle><p className={busy ? 'home-intake__intro-pending' : undefined}>{creating ? '正在创建项目，请稍候…' : busy ? <><span aria-hidden="true" />{activity.title} · 回复还未结束</> : '先让想法成形，再一起把它实现。'}</p></div><div className="home-intake__window-actions">{active && <button type="button" className="home-intake__reset" onClick={() => setResetPending(true)} disabled={busy || creating} title="重新聊一个想法" aria-label="重新聊一个想法"><RotateCcw size={15} /></button>}<button type="button" onClick={() => { setResetPending(false); setDialogOpen(false); }} disabled={creating} title={creating ? '正在创建项目，请稍候' : '关闭后保留对话'} aria-label="关闭创作助手"><X size={19} /></button></div></header>
    {preview && <p className="home-intake__preview">交互预览 · 示例回复，未调用 AI</p>}
    {resetPending && <div className="home-intake__reset-confirm" role="alert"><span>重新开始会清空这次对话和创作方案。</span><button type="button" onClick={() => setResetPending(false)}>继续当前对话</button><button type="button" onClick={() => { restart(); setResetPending(false); }}>清空并重新开始</button></div>}
    <div className="home-intake__workspace">
    <div className="home-intake__chat" ref={conversationRef} onScroll={event => { const log = event.currentTarget; followReply.current = log.scrollHeight - log.scrollTop - log.clientHeight < 48; setAwayFromReply(!followReply.current); }}>
    {!active && <div className="home-intake__welcome"><span className="home-intake__welcome-symbol"><Sparkles size={30} strokeWidth={1.3} /></span><span className="home-intake__eyebrow">把灵感变成下一步</span><h2>不用想完整，<br />我们边聊边完善。</h2><p>说说想讲的故事，或想做的视频。<br />我会整理成一份方案，等你确认后再开始。</p><div className="home-intake__welcome-options">{EXAMPLES.map(({ label, prompt, Icon }) => <button type="button" key={prompt} onClick={() => send(draft.trim() ? `${prompt}\n\n补充：${draft.trim()}` : prompt)}><Icon size={15} /><span>{label}</span><ArrowRight size={14} /></button>)}</div></div>}
    {messages.length > 0 && <div className="home-intake__conversation" role="log" aria-label="创作需求对话" aria-live="polite" aria-busy={busy}>
      {messages.map((item, index) => <div className={`home-intake__message is-${item.role}`} key={index}>
        {item.role === 'assistant' && <span className="home-intake__avatar" aria-hidden="true"><Sparkles size={14} /></span>}
        <span className="sr-only">{item.role === 'user' ? '你' : '创作助手'}：</span>{item.references?.length ? <div className="home-intake__message-body"><p>{item.content}</p><MessageReferences references={item.references} /></div> : <p>{item.content}</p>}
      </div>)}
      {partialReply && <div className="home-intake__message is-assistant is-streaming">
        <span className="home-intake__avatar" aria-hidden="true"><Sparkles size={14} /></span>
        <p>{partialReply}{!busy && <small className="home-intake__partial-note">{stopped ? '已停止 · 回复未完成' : '连接中断 · 回复未完成'}</small>}</p>
      </div>}
    </div>}
    {busy && <CreationActivity activity={activity} />}
    {awayFromReply && <button type="button" className="home-intake__latest" onClick={() => { followReply.current = true; setAwayFromReply(false); conversationRef.current.scrollTop = conversationRef.current.scrollHeight; }}><ArrowDown size={12} />回到最新回复</button>}
    {notice && <div className="home-intake__status" role="status">{notice}</div>}
    {stopped && <div className="home-intake__status" role="status">已停止回复，想法已保留。<button type="button" onClick={retryConversation} disabled={busy || creating}>重新回复</button></div>}
    {ready && <article className="home-intake__review" aria-label="润色后的创作方案">
      <div className="home-intake__polished"><div><span><Sparkles size={12} />创作方案 · 待你确认</span><button type="button" aria-label="修改创作方案" disabled={creating} onClick={() => editBrief('创作方案')}><Pencil size={12} />修改方案</button></div><h3>{decision.title}</h3><p>{decision.brief.polished}</p></div>
      <div className="home-intake__brief-plan"><strong>确认后这样推进</strong><ol>{decision.brief.outline.map(item => <li key={item}>{item}</li>)}</ol></div>
    </article>}
    {ready && <CreationConfirmationBar decision={decision} busy={busy} creating={creating} hasDraft={Boolean(draft.trim() || attachments.items.length)} modes={MODES} onCreate={create} onChangeMode={changeMode}
      referenceCount={conversationReferences(messages).length}
      onParameters={() => send('我想选择创意风格、时长、画幅和清晰度。', 'canvas', { questionMode: 'parameters' })} error={createError} />}
    {error && <div className="home-intake__error" role="alert"><span>{error}</span>{failedMessages && <button type="button" disabled={busy} onClick={retryConversation}>重试沟通</button>}
      {referenceError && <button type="button" disabled={busy} onClick={() => consult((failedMessages || messages).map(({ references, ...item }) => item), modePreference, { retry: true })}>移除对话中的图片后重试</button>}
    </div>}
    {!busy && !error && !stopped && !ready && questions.length > 0 && <CreationQuestions questions={questions} answers={answers} onChange={setAnswers} disabled={creating} draft={draft}
      onSubmit={(text, skip) => send(text, modePreference, { finalize: skip })} />}
    {!busy && !error && !stopped && !ready && !questions.length && messages.length > 0 && suggestions.length > 0 && <div className="home-intake__answers" role="group" aria-label="建议回答">
      <div className="home-intake__answers-label"><CornerDownRight size={13} /><span>选一个方向，或在下方补充</span></div>
      <div className="home-intake__answer-options">{suggestions.map(({ label, prompt }) => <button type="button" key={prompt} disabled={creating} onClick={() => send(draft.trim() ? `${prompt}\n\n补充：${draft.trim()}` : prompt)}><span>{label}</span><ArrowUp size={14} aria-hidden="true" /></button>)}</div>
    </div>}
    {active && !busy && !error && !decision?.brief && messages.at(-1)?.role === 'assistant' && <div className="home-intake__status">继续补齐需求后，再确认创作方案。<button type="button" onClick={() => send(draft.trim() || '请继续核对并补齐我的创作需求，再整理完整方案。')}>继续梳理需求</button></div>}
    </div>
    <div className="home-intake__composer-area">
    <form className={`home-intake__composer${messages.length ? ' is-conversation' : ''}`} aria-label="发送创作需求" onSubmit={event => { event.preventDefault(); send(); }}>
      <label className="sr-only" htmlFor="home-creation-message">描述你的创作想法</label>
      <textarea id="home-creation-message" ref={element => { if (element) { inputRef.current = element; dialogInputRef.current = element; } }} value={draft} onChange={event => setDraft(event.target.value)} maxLength={4000} rows={messages.length ? 1 : 2} disabled={creating}
        placeholder={busy ? '想到什么都可以先写下来…' : ready ? '哪里想调整？我们继续打磨…' : messages.length ? '说说你的选择，或补充细节…' : brand.text('可以跟小影说：我想做一条短视频，主题是…')}
        onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); send(); } }} />
      <ReferenceDrafts attachments={attachments} />
      <div className="home-intake__composer-footer">
        <div className="home-intake__composer-tools"><ReferenceUploadButton onFiles={attachments.add} onBeforeSelect={beforeSelectImages} disabled={creating || attachments.items.length >= 4 || conversationReferences(messages).length + attachments.items.length >= 12} />
        <span>{attachments.blocked ? '图片上传完成后即可发送' : busy ? '你可以先写下补充，等回复结束后再发送' : draft.length > 3600 ? `${draft.length} / 4,000` : 'Enter 发送 · Shift + Enter 换行'}</span></div>
        {busy ? <button type="button" className="home-intake__stop" onClick={() => requestRef.current?.abort()} title="停止回复" aria-label="停止回复"><Square size={12} fill="currentColor" />停止</button>
          : <button type="submit" className="home-intake__send" disabled={creating || attachments.blocked || (!draft.trim() && !attachments.items.length)}
            title={attachments.blocked ? '等待图片上传完成' : !draft.trim() && !attachments.items.length ? '写下你的想法或添加参考图片再发送' : '发送想法'} aria-label={messages.length ? '发送' : '聊聊我的想法'}><ArrowUp size={18} /></button>}
      </div>
    </form>
    </div>
    </div>
    <footer className="home-intake__footnote"><span><Check size={12} />确认方案后，再进入创作</span><span>关闭窗口，对话仍会保留</span></footer>
    </DialogPanel>
    </div>
    </Dialog>
  </>;
}
